/**
 * "At night" screen schedule — `userSettings/{uid}.simpleDashboard.screenSchedule`.
 *
 * Contract shared with the clock (adhan2 1.7.2):
 *   { enabled, mode: 'NIGHT_CLOCK' | 'SCREEN_OFF', offTrigger, onTrigger }
 * Trigger: { type: 'PRAYER' | 'CLOCK_TIME', prayer?, offsetMinutes, clockTimeMinutes? }
 * Missing block → disabled, NIGHT_CLOCK, 23:00 – 05:00. Missing/unknown mode → NIGHT_CLOCK.
 * Writers always write all four fields with upper-case trigger types.
 */

export const SCREEN_SCHEDULE_FIELD = 'simpleDashboard.screenSchedule';

export const SCREEN_MODES = ['NIGHT_CLOCK', 'SCREEN_OFF'];
export const DEFAULT_SCREEN_MODE = 'NIGHT_CLOCK';
export const TRIGGER_PRAYERS = ['Fajr', 'Shuruq', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];

const DEFAULT_FROM_MINUTES = 23 * 60; // 23:00
const DEFAULT_UNTIL_MINUTES = 5 * 60; // 05:00

const clockTrigger = (minutes) => ({ type: 'CLOCK_TIME', offsetMinutes: 0, clockTimeMinutes: minutes });

export const defaultScreenSchedule = Object.freeze({
  enabled: false,
  mode: DEFAULT_SCREEN_MODE,
  offTrigger: Object.freeze(clockTrigger(DEFAULT_FROM_MINUTES)),
  onTrigger: Object.freeze(clockTrigger(DEFAULT_UNTIL_MINUTES)),
});

const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const toInt = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : null);

const normaliseType = (type) => {
  if (typeof type !== 'string') return null;
  const t = type.trim().toLowerCase();
  if (t === 'prayer') return 'PRAYER';
  if (t === 'clock_time' || t === 'clocktime' || t === 'time') return 'CLOCK_TIME';
  return null;
};

const normalisePrayer = (prayer) => {
  if (typeof prayer !== 'string') return null;
  const p = prayer.trim().toLowerCase();
  if (p === 'sunrise' || p === 'shuruq' || p === 'shurooq') return 'Shuruq';
  return TRIGGER_PRAYERS.find((name) => name.toLowerCase() === p) || null;
};

/** One trigger → normalised trigger, or `fallback` when it can't be read. */
export function parseTrigger(raw, fallback) {
  if (!isRecord(raw)) return { ...fallback };
  const type = normaliseType(raw.type);
  const offsetMinutes = toInt(raw.offsetMinutes) ?? 0;
  if (type === 'PRAYER') {
    const prayer = normalisePrayer(raw.prayer);
    if (!prayer) return { ...fallback };
    return { type, prayer, offsetMinutes };
  }
  if (type === 'CLOCK_TIME') {
    const minutes = toInt(raw.clockTimeMinutes);
    if (minutes === null || minutes < 0 || minutes > 1439) return { ...fallback };
    return { type, offsetMinutes, clockTimeMinutes: minutes };
  }
  return { ...fallback };
}

/** Firestore value (possibly missing/partial) → full settings object for the UI. */
export function parseScreenSchedule(raw) {
  const block = isRecord(raw) ? raw : {};
  return {
    enabled: block.enabled === true,
    mode: SCREEN_MODES.includes(block.mode) ? block.mode : DEFAULT_SCREEN_MODE,
    offTrigger: parseTrigger(block.offTrigger, defaultScreenSchedule.offTrigger),
    onTrigger: parseTrigger(block.onTrigger, defaultScreenSchedule.onTrigger),
  };
}

/** Trigger → the exact map written (upper-case type, no stale keys of the other type). */
export const serializeTrigger = (trigger, fallback) => {
  const t = parseTrigger(trigger, fallback);
  return t.type === 'PRAYER'
    ? { type: 'PRAYER', prayer: t.prayer, offsetMinutes: t.offsetMinutes }
    : { type: 'CLOCK_TIME', offsetMinutes: t.offsetMinutes, clockTimeMinutes: t.clockTimeMinutes };
};

/** UI settings → the exact map written to `simpleDashboard.screenSchedule` (all four fields). */
export function serializeScreenSchedule(settings) {
  const s = isRecord(settings) ? settings : {};
  return {
    enabled: s.enabled === true,
    mode: SCREEN_MODES.includes(s.mode) ? s.mode : DEFAULT_SCREEN_MODE,
    offTrigger: serializeTrigger(s.offTrigger, defaultScreenSchedule.offTrigger),
    onTrigger: serializeTrigger(s.onTrigger, defaultScreenSchedule.onTrigger),
  };
}

/** 0..1439 → "HH:mm". */
export const formatClockMinutes = (minutes) => {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/** "HH:mm" → 0..1439, or null. */
export const parseClockMinutes = (text) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(typeof text === 'string' ? text.trim() : '');
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
};

/** Short label for a trigger, e.g. "23:00" or "Isha + 30 min". `prayerName` localises the prayer. */
export const describeTrigger = (trigger, prayerName = (p) => p, minLabel = 'min') => {
  if (trigger.type === 'CLOCK_TIME') return formatClockMinutes(trigger.clockTimeMinutes);
  const name = prayerName(trigger.prayer);
  const off = trigger.offsetMinutes || 0;
  if (off === 0) return name;
  return `${name} ${off > 0 ? '+' : '−'} ${Math.abs(off)} ${minLabel}`;
};

/**
 * `simpleDashboard` maps that only the field-level writer (updateSimpleDashboardField,
 * setDoc mergeFields) may write. A whole-document `setDoc(..., {merge: true})` carrying the
 * context copy could revert a newer edit and merges trigger maps key by key (leaving both
 * `prayer` and `clockTimeMinutes`).
 */
export const FIELD_LEVEL_SIMPLE_DASHBOARD_FIELDS = ['nightBrightness', 'screenSchedule', 'childLock'];

/** Copy of a settings write without the given `simpleDashboard` fields (input not mutated). */
export function stripSimpleDashboardFields(settings, fields = FIELD_LEVEL_SIMPLE_DASHBOARD_FIELDS) {
  if (!isRecord(settings) || !isRecord(settings.simpleDashboard)) return settings;
  if (!fields.some((f) => f in settings.simpleDashboard)) return settings;
  const simpleDashboard = { ...settings.simpleDashboard };
  for (const f of fields) delete simpleDashboard[f];
  return { ...settings, simpleDashboard };
}

export const stripScreenSchedule = (settings) => stripSimpleDashboardFields(settings, ['screenSchedule']);
